import { useState } from 'react';
import { useStore } from 'zustand';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useUnityBuildStore } from '../../stores/store';
import { useHandleSetChangeOrder } from './helpers';
import { CreateChangeOrder } from './CreateChangeOrder';
import { useFetchContracts } from '../../hooks/useFetchContracts';
import { useFetchChangeOrders } from '../../hooks/useFetchChangeOrders';
import { useFetchEstimateStatuses } from '../../hooks/useFetchProjectEstimates';
import { useDeleteChangeOrderMutation } from '../../hooks/useCreateChangeOrderMutation';
import { ChangeOrder } from '../../api/protosCompiled/changeOrder/changeOrder_pb';
import { getStatusChipProps, getStatusDescription } from './ListEstimates';
import { formatDateForDisplay } from '../../utils/dateUtils';

// Import responsive utilities
import { useResponsive, useOrientation, ResponsiveHelpers } from '../../theme';

interface ListChangeOrdersProps {
  contractId?: string;
  estimateId?: string;
  onBack?: () => void;
}

export function ListChangeOrders({ contractId, estimateId, onBack }: ListChangeOrdersProps = {}) {
  const theme = useTheme();
  const responsive = useResponsive() as ResponsiveHelpers;
  const { isMobile, isTablet } = responsive;
  const { isLandscape } = useOrientation();

  const store = useStore(useUnityBuildStore);
  const { refetch } = useFetchContracts();
  const [searchTerm, setSearchTerm] = useState('');

  // Add state for success and error notifications
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [changeOrderToDelete, setChangeOrderToDelete] = useState<ChangeOrder.AsObject | null>(null);

  // Delete mutation hook
  const deleteChangeOrderMutation = useDeleteChangeOrderMutation();

  // Use the local estimateId if provided, otherwise fall back to global state
  const effectiveEstimateId =
    estimateId || store.selectedEstimateIdForChangeOrder || store.selectedEstimate?.estimateid;
  const {
    data: changeOrderList,
    isLoading,
    refetch: refetchChangeOrders,
  } = useFetchChangeOrders(effectiveEstimateId);

  const handleSetValue = useHandleSetChangeOrder();

  const { setSelectedChangeOrder, setIsOpeningViewport } = useStore(useUnityBuildStore);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(event.target.value);

  // Data normalization: Flatten the nested structure
  const normalizedChangeOrders = changeOrderList?.map(changeOrderResponse => {
    const changeOrder = changeOrderResponse.changeorder;
    return {
      ...changeOrder,
      contractname: changeOrderResponse.contractname,
      contractdescription: changeOrderResponse.contractdescription,
    };
  });

  const handleSelectChangeOrder = (changeOrder: ChangeOrder.AsObject) => {
    setIsOpeningViewport(true);
    setSelectedChangeOrder(changeOrder);
  };

  const handleCloseForm = () => {
    refetch();
    store.setIsViewingChangeOrderForm(false);
  };

  // Notification handlers
  const handleCloseNotification = () => {
    setShowSuccessNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (changeOrder: ChangeOrder.AsObject) => {
    setChangeOrderToDelete(changeOrder);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChangeOrderToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (changeOrderToDelete) {
      try {
        await deleteChangeOrderMutation.mutateAsync(changeOrderToDelete);
        setDeleteDialogOpen(false);
        setChangeOrderToDelete(null);
        setShowSuccessNotification(true);
        setSuccessMessage('Change order deleted successfully!');

        // Manually refetch the change orders list to update the UI
        refetchChangeOrders();
      } catch (error) {
        console.error('Error deleting change order:', error);
        setDeleteDialogOpen(false);
        setChangeOrderToDelete(null);

        // Show user-friendly error message
        setShowErrorNotification(true);
        setDeleteErrorMessage(
          'This change order cannot be deleted. It may be referenced by other records in the system.'
        );
      }
    }
  };

  const filteredChangeOrders = normalizedChangeOrders?.filter(
    change_order =>
      change_order.contractid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      change_order.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to determine which table columns to show based on screen size
  const getVisibleColumns = () => {
    if (isMobile && !isLandscape) {
      // In portrait mode on mobile, show minimal columns
      return {
        contractId: true,
        changeName: true,
        status: true,
        dateCreated: false,
        dueDate: false,
        actions: true,
      };
    } else if (isMobile && isLandscape) {
      // In landscape mode on mobile, show more columns
      return {
        contractId: true,
        changeName: true,
        status: true,
        dateCreated: true,
        dueDate: false,
        actions: true,
      };
    } else if (isTablet) {
      // On tablets show most columns
      return {
        contractId: true,
        changeName: true,
        status: true,
        dateCreated: true,
        dueDate: true,
        actions: true,
      };
    }
    // On desktop show all columns
    return {
      contractId: true,
      changeName: true,
      status: true,
      dateCreated: true,
      dueDate: true,
      actions: true,
    };
  };

  const visibleColumns = getVisibleColumns();

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} role="region" aria-label="Change orders list">
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          mb: { xs: 2, sm: 3 },
          alignItems: 'center',
          flexDirection: isMobile && !isLandscape ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
        }}
      >
        {onBack && (
          <Button
            variant="outlined"
            onClick={onBack}
            startIcon={<ArrowBackIcon aria-hidden="true" />}
            aria-label="Back to contracts"
            sx={{ height: '40px' }}
          >
            Back to Contracts
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSetValue('create', null)}
          startIcon={<AddIcon aria-hidden="true" />}
          aria-label="Create new change order"
          sx={{
            height: '40px',
            width: isMobile && !isLandscape ? '100%' : 'auto',
          }}
        >
          Create Change Order
        </Button>
        <TextField
          placeholder="Search Change Orders..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: <SearchIcon aria-hidden="true" sx={{ mr: 1 }} />,
          }}
          id="change-order-search"
          aria-label="Search change orders"
          sx={{
            flexGrow: 1,
            minWidth: isMobile && !isLandscape ? '100%' : '250px',
            maxWidth: isMobile ? '100%' : '400px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.23)'
                    : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      </Box>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', my: 4 }}
          role="status"
          aria-label="Loading change orders"
        >
          <CircularProgress aria-label="Loading" />
        </Box>
      ) : filteredChangeOrders && filteredChangeOrders.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: isMobile ? '60vh' : '70vh',
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1],
            borderRadius: 1,
            overflow: 'auto',
          }}
        >
          <Table
            stickyHeader
            aria-label="Change orders table"
            role="grid"
            size={isMobile ? 'small' : 'medium'}
          >
            <TableHead>
              <TableRow>
                {visibleColumns.contractId && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Change Order ID
                  </TableCell>
                )}
                {visibleColumns.changeName && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                      width: isMobile ? '35%' : '40%',
                    }}
                  >
                    Change Name
                  </TableCell>
                )}
                {visibleColumns.status && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: isMobile ? '20%' : '15%',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Status
                  </TableCell>
                )}
                {visibleColumns.dateCreated && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Created
                  </TableCell>
                )}
                {visibleColumns.dueDate && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Due Date
                  </TableCell>
                )}
                {visibleColumns.actions && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredChangeOrders.map(item => (
                <TableRow
                  key={item.changeorderid}
                  onClick={() => handleSelectChangeOrder(item as any)}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.1 : 0.05
                      ),
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.02)
                          : alpha(theme.palette.common.black, 0.02),
                    },
                  }}
                  role="row"
                  aria-label={`Change order: ${item.name || 'Unnamed change order'}`}
                >
                  {visibleColumns.contractId && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {item.customid}
                    </TableCell>
                  )}
                  {visibleColumns.changeName && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {item.name}
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {item.changeorderstatusname ? (
                        <Tooltip
                          title={getStatusDescription(item.changeorderstatusname)}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={item.changeorderstatusname}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                              ...getStatusChipProps(item.changeorderstatusname, theme),
                              fontWeight: 500,
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                            }}
                          />
                        </Tooltip>
                      ) : (
                        ''
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.dateCreated && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {formatDateForDisplay(item.datecreated)}
                    </TableCell>
                  )}
                  {visibleColumns.dueDate && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {formatDateForDisplay(item.duedate)}
                    </TableCell>
                  )}
                  {visibleColumns.actions && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip
                          title={`Edit change order details for ${item.name || 'this change order'}`}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              handleSetValue('update', (item as any) ?? null);
                            }}
                            color="primary"
                            aria-label={`Edit change order ${item.name || 'unnamed change order'}`}
                          >
                            <EditIcon fontSize="small" aria-hidden="true" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={`Delete change order ${item.name || 'this change order'}`}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              handleDeleteClick(item as ChangeOrder.AsObject);
                            }}
                            color="error"
                            aria-label={`Delete change order ${item.name || 'unnamed change order'}`}
                          >
                            <DeleteIcon fontSize="small" aria-hidden="true" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            p: { xs: 2, sm: 4 },
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: theme.shadows[1],
          }}
          role="status"
          aria-live="polite"
        >
          <Typography variant="body1" color="text.secondary">
            {searchTerm
              ? 'No change orders match your search criteria.'
              : 'No change orders available.'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={store.isViewingChangeOrderForm}
        onClose={handleCloseForm}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === 'dark' ? '#2c2c2c' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            maxHeight: '75%',
          },
        }}
        aria-labelledby="change-order-dialog-title"
      >
        <DialogContent
          sx={{
            p: isMobile ? { xs: 1, sm: 2 } : 3,
          }}
        >
          {store.isViewingChangeOrderForm && <CreateChangeOrder estimateId={effectiveEstimateId} />}
        </DialogContent>
      </Dialog>

      {/* Success notification */}
      <Snackbar
        open={showSuccessNotification}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error notification */}
      <Snackbar
        open={showErrorNotification}
        autoHideDuration={6000}
        onClose={handleCloseErrorNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorNotification} severity="error" sx={{ width: '100%' }}>
          {deleteErrorMessage}
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: theme.palette.error.main }}>
          Delete Change Order
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description" variant="body1">
            Are you sure you want to delete the change order "
            {changeOrderToDelete?.name || 'Unnamed Change Order'}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            color="primary"
            variant="outlined"
            disabled={deleteChangeOrderMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteChangeOrderMutation.isLoading}
            startIcon={
              deleteChangeOrderMutation.isLoading ? <CircularProgress size={16} /> : undefined
            }
          >
            {deleteChangeOrderMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
